import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import { useOrganisation } from "../lib/organisation";
import { useAuth } from "../lib/auth";
import { Button } from "../components/button";
import { ConfirmModal } from "../components/modal";
import {
  appContainer,
  pageContent,
  pageSection,
  pageSectionHeader,
  pageSectionTitle,
  pageSectionSubtitle,
  pageEmpty,
  eventsList,
  eventItem,
  eventContent,
  eventName,
  eventDate,
  loading,
  error,
  invitationLinkRow,
  invitationLinkCode,
} from "@/styles/shared";

const youBadge = css`
  margin-left: 8px;
  font-size: 0.85em;
  opacity: 0.6;
`;

const invitationsSection = css`
  margin-bottom: 32px;
`;

const dimmedItem = css`
  opacity: 0.6;
`;

type Member = {
  email: string;
  joinedAt: string;
};

type Invitation = {
  code: string;
  expiresAt: string;
  usedBy: string | null;
  usedAt: string | null;
};

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getInvitationLink(code: string): string {
  return `${window.location.origin}/join/${code}`;
}

export function MembersPage() {
  const { t, i18n } = useTranslation();
  const { currentOrganisation } = useOrganisation();
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [invitationToDelete, setInvitationToDelete] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);

  const fetchMembers = async () => {
    try {
      setIsLoading(true);
      const result = await trpcClient.listOrganisationMembers.query();
      setMembers(result);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load members");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoadingInvitations(true);
      const result = await trpcClient.listOrganisationInvitations.query();
      setInvitations(result);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load invitations");
    } finally {
      setLoadingInvitations(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchInvitations();
  }, [currentOrganisation?.id]);

  const handleGenerateInvitation = async () => {
    setGenerating(true);
    try {
      await trpcClient.createInvitation.mutate();
      await fetchInvitations();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to generate invitation");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteInvitation = async (code: string) => {
    setDeletingCode(code);
    try {
      await trpcClient.deleteInvitation.mutate({ code });
      await fetchInvitations();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to delete invitation");
    } finally {
      setDeletingCode(null);
      setInvitationToDelete(null);
    }
  };

  const handleCopyLink = async (code: string) => {
    const link = getInvitationLink(code);
    await navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRemoveMember = async (email: string) => {
    setRemovingEmail(email);
    try {
      await trpcClient.removeMember.mutate({ email });
      await fetchMembers();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to remove member");
    } finally {
      setRemovingEmail(null);
      setMemberToRemove(null);
    }
  };

  return (
    <div css={appContainer}>
      <main css={pageContent}>
        <section css={pageSection}>
          <div css={pageSectionHeader}>
            <h3 css={pageSectionTitle}>{t("members.title")}</h3>
            <Button
              onClick={handleGenerateInvitation}
              variant="primary"
              size="sm"
              disabled={generating}
            >
              {generating ? t("members.generating") : t("members.generateInvitation")}
            </Button>
          </div>

          {err && <p css={error}>{t("common.error", { message: err })}</p>}

          {loadingInvitations && <p css={loading}>{t("members.loadingInvitations")}</p>}

          {!loadingInvitations && invitations.length > 0 && (
            <div css={invitationsSection}>
              <h4 css={pageSectionSubtitle}>{t("members.invitationsTitle")}</h4>
              <ul css={eventsList}>
                {invitations.map((inv) => {
                  const isUsed = inv.usedBy !== null;
                  const isExpired = !isUsed && new Date(inv.expiresAt) < new Date();
                  return (
                    <li
                      key={inv.code}
                      css={[eventItem, (isUsed || isExpired) && dimmedItem]}
                    >
                      <div css={eventContent}>
                        <div css={invitationLinkRow}>
                          <code css={invitationLinkCode}>
                            {getInvitationLink(inv.code)}
                          </code>
                          {!isUsed && !isExpired && (
                            <Button
                              onClick={() => handleCopyLink(inv.code)}
                              variant="secondary"
                              size="xs"
                            >
                              {copiedCode === inv.code
                                ? t("members.copied")
                                : t("members.copyLink")}
                            </Button>
                          )}
                          <Button
                            onClick={() => setInvitationToDelete(inv.code)}
                            variant="danger"
                            size="xs"
                            disabled={deletingCode === inv.code}
                          >
                            {deletingCode === inv.code
                              ? t("members.deleting")
                              : t("members.deleteInvitation")}
                          </Button>
                        </div>
                        <p css={eventDate}>
                          {isUsed
                            ? t("members.used")
                            : isExpired
                              ? t("members.expired")
                              : t("members.expiresAt", {
                                  date: formatDate(inv.expiresAt, i18n.language),
                                })}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {isLoading && <p css={loading}>{t("members.loading")}</p>}
          {!isLoading && members.length === 0 && <p css={pageEmpty}>{t("members.empty")}</p>}
          {!isLoading && members.length > 0 && (
            <>
              <h4 css={pageSectionSubtitle}>{t("members.listTitle")}</h4>
              <ul css={eventsList}>
                {members.map((member) => {
                  const isCurrentUser = member.email === user?.email;
                  return (
                    <li key={member.email} css={eventItem}>
                      <div css={eventContent}>
                        <h4 css={eventName}>
                          {member.email}
                          {isCurrentUser && (
                            <span css={youBadge}>({t("members.you")})</span>
                          )}
                        </h4>
                        <p css={eventDate}>
                          {t("members.joinedAt", {
                            date: formatDate(member.joinedAt, i18n.language),
                          })}
                        </p>
                      </div>
                      {!isCurrentUser && (
                        <Button
                          onClick={() => setMemberToRemove(member.email)}
                          variant="danger"
                          size="xs"
                          disabled={removingEmail === member.email}
                        >
                          {removingEmail === member.email
                            ? t("members.removing")
                            : t("members.removeMember")}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>
      </main>

      <ConfirmModal
        open={invitationToDelete !== null}
        onClose={() => setInvitationToDelete(null)}
        onConfirm={() => invitationToDelete && handleDeleteInvitation(invitationToDelete)}
        title={t("common.confirm")}
        message={t("members.confirmDeleteInvitation")}
        variant="danger"
        loading={deletingCode !== null}
      />

      <ConfirmModal
        open={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={() => memberToRemove && handleRemoveMember(memberToRemove)}
        title={t("common.confirm")}
        message={t("members.confirmRemoveMember")}
        variant="danger"
        loading={removingEmail !== null}
      />
    </div>
  );
}
